import { useEffect, useMemo, type PropsWithChildren } from 'react';
import { CssBaseline, ThemeProvider } from '@mui/material';
import { QueryClient, QueryClientProvider, useQueryClient } from '@tanstack/react-query';
import { BrowserRouter, useNavigate } from 'react-router-dom';
import { createDikonekTheme } from '@dikonek/ui-common';
import { AuthProvider, useAuth } from '../auth/AuthContext';
import { setApiAuthHandlers } from '../api/client';

function AuthBridge({ children }: PropsWithChildren) {
  const { getToken, refresh, logout } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  useEffect(() => {
    setApiAuthHandlers({
      getToken,
      refresh,
      onAuthFailure: () => {
        logout();
        queryClient.clear();
        navigate('/login', { replace: true });
      },
    });
    return () => setApiAuthHandlers(null);
  }, [getToken, refresh, logout, navigate, queryClient]);
  return <>{children}</>;
}

/**
 * Local-auth + API-token wiring for the internal app. NOT part of AppProviders:
 * the public client share page (/share/:token) must render without any auth
 * state, so App mounts this only on internal routes (incl. /login).
 */
export function AuthProviders({ children }: PropsWithChildren) {
  return (
    <AuthProvider>
      <AuthBridge>{children}</AuthBridge>
    </AuthProvider>
  );
}

const createClient = () =>
  new QueryClient({
    defaultOptions: { queries: { refetchOnWindowFocus: false, retry: 1 } },
  });

export function AppProviders({ children }: PropsWithChildren) {
  const queryClient = useMemo(createClient, []);
  const theme = useMemo(() => createDikonekTheme('light'), []);

  return (
    <BrowserRouter>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          {children}
        </ThemeProvider>
      </QueryClientProvider>
    </BrowserRouter>
  );
}
